import { useState } from 'react';
import { Activity, InfoCircle, Refresh } from 'iconsax-reactjs';
import {
  RSI_DEFAULT_PERIOD,
  RSI_SCAN_BARS,
  RSI_TIMEFRAMES,
  type Divergence,
  type Timeframe,
} from '@pipyar/core';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { RsiChart } from '@/components/chart/RsiChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { TimeframeButtons } from '@/components/chart/TimeframeButtons';
import { useRsi, useRsiSummary } from './api';
import { RsiSummaryGrid, biasChip, biasLabel } from './RsiSummaryGrid';
import { useI18n } from '@/lib/i18n';

const PERIOD_PRESETS = [7, 14, 21];

export function RsiPage() {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [timeframe, setTimeframe] = useState<Timeframe>('H1');
  const [period, setPeriod] = useState(RSI_DEFAULT_PERIOD);
  const [showHidden, setShowHidden] = useState(true);

  const { symbol } = useSymbol();

  const rsi = useRsi(symbol, timeframe, period);
  const summary = useRsiSummary(symbol, period);

  const analysis = rsi.data?.analysis;
  const allDivergences = analysis?.divergences ?? [];
  const divergences = showHidden
    ? allDivergences
    : allDivergences.filter((d) => d.kind === 'regular');

  const error = rsi.error;
  const isBusy = rsi.isFetching;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('rsi.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {symbol ?? '—'} · below 30 bullish, above 70 bearish — very bullish/bearish with a matching divergence
          </p>
        </div>

        <div className="ms-auto flex items-center gap-2">
          <div className="hidden items-center gap-1 sm:flex">
            <span className="me-1 text-xs text-muted-foreground">{t('rsi.period')}</span>
            {PERIOD_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={cn(
                  'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  period === p
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            aria-pressed={showHidden}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              showHidden
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            Hidden
          </button>

          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common.refresh')}
            onClick={() => {
              void rsi.refetch();
              void summary.refetch();
            }}
          >
            <Refresh size={18} className={isBusy ? 'animate-spin' : undefined} />
          </Button>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : 'Could not load RSI data.'}</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_19rem]">
        {/* min-w-0 is load-bearing: a 1fr grid column defaults to
            min-width:auto, so it cannot shrink below its content. Once the
            chart canvas has been sized to the viewport by full screen, the
            column would stay that wide on exit and the page would scroll
            sideways forever. */}
        <section className="order-2 min-w-0 space-y-3 lg:order-1">
          <div className="flex flex-wrap items-center gap-1">
            <TimeframeButtons value={timeframe} onChange={setTimeframe} options={RSI_TIMEFRAMES} />

            <div className="ms-auto flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-4 rounded bg-bull" /> bullish
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-4 rounded bg-bear" /> bearish
              </span>
              <span className="opacity-70">{t('rsi.dashedHidden')}</span>
            </div>
          </div>

          <ChartFrame
            label={t('rsi.chart')}
            fullscreenToolbar={
              <TimeframeButtons value={timeframe} onChange={setTimeframe} options={RSI_TIMEFRAMES} compact />
            }
            className="group/frame h-[34rem] overflow-hidden glass rounded-2xl"
          >
            {rsi.isPending ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : (rsi.data?.candles.length ?? 0) === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                No candles for {timeframe} yet.
              </div>
            ) : (
              <RsiChart
                className="h-full w-full"
                candles={rsi.data!.candles}
                points={rsi.data!.points}
                divergences={divergences}
                digits={rsi.data!.digits}
                theme={theme}
                resetKey={`${symbol}-${timeframe}-${period}`}
              />
            )}
          </ChartFrame>

          <p className="text-xs text-muted-foreground">
            RSI({period}) over the last {(RSI_SCAN_BARS[timeframe] ?? 1000).toLocaleString()}{' '}
            {timeframe} bars · {rsi.data?.points.length.toLocaleString() ?? 0} values ·{' '}
            {allDivergences.filter((d) => d.kind === 'regular').length} regular and{' '}
            {allDivergences.filter((d) => d.kind === 'hidden').length} hidden divergences
          </p>
        </section>

        {/* ---------- sidebar ---------- */}
        <aside className="order-1 space-y-4 lg:order-2">
          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2.5 flex items-center gap-2 text-sm font-medium">
              <Activity size={16} variant="Bold" className="text-primary" />
              All timeframes
            </h2>
            <RsiSummaryGrid
              rows={summary.data?.timeframes}
              isLoading={summary.isPending}
              active={timeframe}
              onSelect={setTimeframe}
            />
          </div>

          {analysis && (
            <div className="glass rounded-2xl p-3">
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-sm font-medium">{timeframe} analysis</h2>
                <span
                  className={cn(
                    'ms-auto rounded px-1.5 py-0.5 text-[11px] font-semibold capitalize',
                    biasChip(analysis),
                  )}
                >
                  {biasLabel(analysis)}
                </span>
              </div>

              <div className="mb-2.5 flex items-baseline gap-3">
                <span className="tabular text-2xl font-semibold">{analysis.value ?? '—'}</span>
                <span className="text-xs capitalize text-muted-foreground">{analysis.zone}</span>
                <span className="tabular ms-auto text-xs text-muted-foreground">
                  score {analysis.score > 0 ? '+' : ''}
                  {analysis.score}
                </span>
              </div>

              <ul className="space-y-1.5">
                {analysis.reasons.map((r) => (
                  <li key={r} className="flex gap-1.5 text-xs leading-snug text-muted-foreground">
                    <span className="mt-1 size-1 shrink-0 rounded-full bg-muted-foreground/50" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {allDivergences.length > 0 && (
            <div className="glass rounded-2xl p-3">
              <h2 className="mb-2 text-sm font-medium">{t('rsi.divergences')}</h2>
              <ul className="space-y-1.5">
                {[...allDivergences]
                  .reverse()
                  .slice(0, 8)
                  .map((d) => (
                    <DivergenceRow key={d.id} d={d} digits={rsi.data?.digits ?? 2} />
                  ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function DivergenceRow({ d, digits }: { d: Divergence; digits: number }) {
  return (
    <li className="flex items-baseline gap-2 text-xs">
      <span
        className={cn(
          'rounded px-1 py-px text-[10px] font-medium',
          d.bias === 'bullish' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
        )}
      >
        {d.kind === 'hidden' ? 'hidden' : 'regular'}
      </span>
      <span className="tabular text-muted-foreground">{d.toPrice.toFixed(digits)}</span>
      <span className="tabular ms-auto text-muted-foreground">{d.rsiDelta} RSI</span>
    </li>
  );
}
